import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { AppShell } from "@/components/app/AppShell";
import { ProductSyncBar } from "@/components/app/ProductSyncBar";
import { ProductDetailModal } from "./ProductDetailModal";
import { useAuth } from "@/lib/AuthContext";
import { useBrand, useProducts, useWorkspace } from "@/lib/queries";
import type { ProductRecord } from "@/lib/queries";
import styles from "./Products.module.css";

export function Products() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const brandQuery = useBrand(ws, brand);
  const wsQuery = useWorkspace(ws);
  const productsQuery = useProducts(ws, brand);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/products`} replace />;
  }

  const b = brandQuery.data?.brand;
  const role = wsQuery.data?.role;
  const canManageBrands = role === "owner" || role === "admin";
  // Linking media to a product is a content action (same tier as the media
  // library / generation), so editors can do it too — matches the server's
  // `generate` gate on the PATCH endpoint.
  const canEditMedia = role === "owner" || role === "admin" || role === "editor";
  const products = productsQuery.data?.products ?? [];
  const summary = productsQuery.data?.summary ?? null;
  const [selected, setSelected] = useState<ProductRecord | null>(null);

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn href={`/w/${ws}/b/${brand}`} variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />}>
          Back to brand
        </Btn>

        <header className={styles.head}>
          <h1 className={styles.title}>
            <Icon name="store" size={20} /> Products{b ? ` · ${b.name}` : ""}
          </h1>
        </header>

        {ws && brand && (
          <ProductSyncBar ws={ws} brand={brand} canManageBrands={canManageBrands} />
        )}

        {productsQuery.isLoading && <p className={styles.muted}>Loading products…</p>}
        {productsQuery.error && (
          <p className={styles.error}>Couldn’t load products: {productsQuery.error.message}</p>
        )}

        {!productsQuery.isLoading && !productsQuery.error && products.length === 0 && (
          <p className={styles.empty}>
            {summary
              ? "Last sync found no products. Check the Site URL on the brain, or that the store exposes a sitemap / product markup."
              : canManageBrands
                ? "No products yet. Set a website / Shopify / WooCommerce source on the brain, then “Crawl now”."
                : "No products yet. Once a manager syncs this brand’s product source, they’ll appear here."}
          </p>
        )}

        {products.length > 0 && (
          <ul className={styles.grid}>
            {products.map((p) => (
              <ProductCard key={p.handle} product={p} onOpen={() => setSelected(p)} />
            ))}
          </ul>
        )}
      </div>

      <ProductDetailModal
        key={selected?.handle}
        product={selected}
        ws={ws}
        brand={brand}
        canEdit={canEditMedia}
        onClose={() => setSelected(null)}
      />
    </AppShell>
  );
}

function ProductCard({
  product,
  onOpen,
}: {
  product: ProductRecord;
  onOpen: () => void;
}) {
  const img = product.sourceImageUrls?.[0];
  return (
    <li className={`${styles.card} ${product.available ? "" : styles.unavailable}`}>
      <button type="button" className={styles.thumb} onClick={onOpen} aria-haspopup="dialog">
        {img ? (
          <img src={img} alt="" loading="lazy" />
        ) : (
          <span className={styles.noImage}>
            <Icon name="image" size={20} />
          </span>
        )}
      </button>
      <div className={styles.body}>
        <button type="button" className={styles.name} onClick={onOpen} aria-haspopup="dialog">
          {product.title}
        </button>
        <div className={styles.meta}>
          {product.priceCents != null && (
            <span className={styles.price}>{formatPrice(product.priceCents, product.currency)}</span>
          )}
          <span className={product.available ? styles.inStock : styles.outStock}>
            {product.available ? "In stock" : "Out of stock"}
          </span>
        </div>
        {product.collections?.length > 0 && (
          <div className={styles.chips}>
            {product.collections.slice(0, 3).map((c) => (
              <span key={c} className={styles.chip}>{c}</span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

function formatPrice(cents: number, currency?: string): string {
  const amount = cents / 100;
  try {
    return amount.toLocaleString(undefined, { style: "currency", currency: currency || "USD" });
  } catch {
    return `${amount.toFixed(2)} ${currency ?? ""}`.trim();
  }
}
